// MCP Security — Alerts & integrations.
//
// Three stacked cards:
//   1) MCP alert conditions — toggle each, threshold input (where
//      conditions.threshold is numeric), chip-row channel picker.
//      One row per existing AlertRule (filtered to the 5 MCP_* types);
//      a "+ New condition" row at the bottom creates a rule.
//   2) Channels — tile per shared Integrations-framework channel.
//   3) SIEM export — per-endpoint stream toggles. Each PATCHes
//      /mcp/endpoints/:id/siem.
//
// Wired through CP routes added 2026-10-11:
//   GET    /mcp/sites/:site_uid/alerts
//   POST   /mcp/sites/:site_uid/alerts
//   PATCH  /mcp/sites/:site_uid/alerts/:ruleId
//   DELETE /mcp/sites/:site_uid/alerts/:ruleId
//   PATCH  /mcp/endpoints/:id/siem

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, Alert, Button, TextField, Switch, MenuItem, Select, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import axios from "../../../../../utils/axios/v1/userAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import tokens from "./_shared/tokens";
import { secondaryButtonStyle, dangerButtonStyle } from "./_shared/buttonStyles";

// Condition catalogue — human-facing labels for the 5 MCP alert types.
// Keys match the server's MCP_ALERT_CONDITION_TYPES enum (mcp_policy.js).
const CONDITION_META = {
  MCP_INJECTION_BLOCK_RATE_HIGH: {
    label: "Injection block spike",
    desc: "More than",
    unit: "blocks / 5 min",
    thresholdField: "threshold",
    defaultThr: 20,
    defaultSev: "warning",
  },
  MCP_DRIFT_ACTIVATED: {
    label: "Drift hold opened",
    desc: "A new drift_hold on any endpoint",
    unit: null,
    thresholdField: null,
    defaultThr: null,
    defaultSev: "info",
  },
  MCP_TAINTED_SESSION_WRITE: {
    label: "Tainted session write",
    desc: "A session fingerprint flagged for taint attempted a write tool",
    unit: null,
    thresholdField: null,
    defaultThr: null,
    defaultSev: "warning",
  },
  MCP_NEW_AGENT_ID: {
    label: "New agent appeared",
    desc: "A previously unseen agent_id called a tool",
    unit: null,
    thresholdField: null,
    defaultThr: null,
    defaultSev: "info",
  },
  MCP_CLASSIFIER_HIGH_SCORE: {
    label: "High-risk classifier score",
    desc: "Classifier risk ≥",
    unit: "×",
    thresholdField: "score",
    defaultThr: 2.0,
    defaultSev: "warning",
  },
};
const CONDITION_ORDER = [
  "MCP_INJECTION_BLOCK_RATE_HIGH",
  "MCP_DRIFT_ACTIVATED",
  "MCP_TAINTED_SESSION_WRITE",
  "MCP_NEW_AGENT_ID",
  "MCP_CLASSIFIER_HIGH_SCORE",
];

const CHANNELS_AVAILABLE = ["slack", "pagerduty", "opsgenie", "email", "webhook", "teams"];

const CHANNEL_TILES = [
  { name: "Slack", channel: "slack" },
  { name: "PagerDuty", channel: "pagerduty" },
  { name: "Opsgenie", channel: "opsgenie" },
  { name: "Email", channel: "email" },
  { name: "Webhook", channel: "webhook" },
  { name: "Microsoft Teams", channel: "teams" },
];

const SIEM_PREVIEW = `{
  "ts":"2026-10-08T11:42:19Z","site_uid":"<site>",
  "endpoint_id":"ep_01HNA...","tool":"update_ticket",
  "outcome":"scope_denied","session_fp":"9ff21048",
  "agent_id":"ag_fin","client_ip":"203.0.113.42",
  "classes":["scope_mismatch"]
}`;

const SEV_STYLE = {
  critical: { bg: tokens.danger.bg, fg: tokens.danger[600] },
  warning:  { bg: tokens.warning.bg, fg: tokens.secondary[500] },
  info:     { bg: tokens.info.bg, fg: tokens.info[500] },
};

export default function McpAlerts() {
  const { effectiveSiteUid } = useGlobalSite();
  const { showNotification } = useNotification();
  const [rules, setRules] = useState(null); // null = loading
  const [siem, setSiem] = useState({});
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingIds, setSavingIds] = useState(new Set());

  const markSaving = (id, on) =>
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const load = useCallback(async () => {
    if (!effectiveSiteUid) return;
    setErr(null);
    try {
      const r = await axios.get(`mcp/sites/${effectiveSiteUid}/alerts`);
      setRules(r.data?.items || []);
      setSiem(r.data?.siem || {});
    } catch (e) {
      setErr(e?.response?.data?.error || e?.response?.data?.message || e.message);
      setRules([]);
    }
  }, [effectiveSiteUid]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const patchRule = async (rule, data) => {
    markSaving(rule.id, true);
    try {
      await axios.patch(`mcp/sites/${effectiveSiteUid}/alerts/${rule.id}`, data);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "update failed" });
    } finally {
      markSaving(rule.id, false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete alert condition "${rule.name}"?`)) return;
    markSaving(rule.id, true);
    try {
      await axios.delete(`mcp/sites/${effectiveSiteUid}/alerts/${rule.id}`);
      setRefreshKey((k) => k + 1);
      showNotification({ severity: "success", message: "Alert deleted" });
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "delete failed" });
    } finally {
      markSaving(rule.id, false);
    }
  };

  const createRule = async (alertType) => {
    const meta = CONDITION_META[alertType];
    const conditions = meta.thresholdField ? { [meta.thresholdField]: meta.defaultThr } : {};
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/alerts`, {
        name: meta.label,
        alertType,
        enabled: true,
        severity: meta.defaultSev,
        conditions,
        channels: [],
        notifyEmails: [],
      });
      setRefreshKey((k) => k + 1);
      showNotification({ severity: "success", message: `Added "${meta.label}"` });
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "create failed" });
    }
  };

  const toggleSiem = async (endpoint_id, enabled) => {
    try {
      await axios.patch(`mcp/endpoints/${endpoint_id}/siem`, { enabled });
      setSiem((prev) => ({ ...prev, [endpoint_id]: { ...prev[endpoint_id], siem_enabled: enabled } }));
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "siem toggle failed" });
    }
  };

  const existingTypes = new Set((rules || []).map((r) => r.alertType));
  const missing = CONDITION_ORDER.filter((t) => !existingTypes.has(t));

  const loading = rules === null;

  return (
    <>
      <Helmet title="MCP Security — Alerts" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Alerts & integrations"
          description="Where MCP decisions go when they matter. Routed through the shared Integrations framework."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to configure alerts.</Alert>}
              {err && <Alert severity="error">{err}</Alert>}

              {/* Alert conditions */}
              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}` }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>MCP alert conditions</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                    Routed through the shared Integrations framework. One rule per MCP condition type.
                  </Typography>
                </Box>

                {loading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}

                {!loading && (rules || []).map((a) => {
                  const meta = CONDITION_META[a.alertType] || { label: a.alertType, desc: "", unit: null, thresholdField: null, defaultSev: "info" };
                  const sev = SEV_STYLE[a.severity] || SEV_STYLE.info;
                  const thrVal = meta.thresholdField ? (a.conditions?.[meta.thresholdField] ?? "") : null;
                  return (
                    <Stack
                      key={a.id}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[150]}`, flexWrap: "wrap" }}
                    >
                      <Switch
                        checked={!!a.enabled}
                        onChange={() => patchRule(a, { enabled: !a.enabled })}
                        size="small"
                        disabled={savingIds.has(a.id)}
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.primaryIndigo }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.primaryIndigo } }}
                      />
                      <Box sx={{ flex: 1, minWidth: 260, opacity: a.enabled ? 1 : 0.55 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{a.name || meta.label}</Typography>
                          <Select
                            value={a.severity}
                            size="small"
                            onChange={(e) => patchRule(a, { severity: e.target.value })}
                            sx={{ height: 22, fontSize: 11, padding: 0, "& .MuiSelect-select": { padding: "0 24px 0 8px", fontSize: 11, background: sev.bg, color: sev.fg, borderRadius: "999px", fontFamily: tokens.fontMono } }}
                          >
                            <MenuItem value="critical">critical</MenuItem>
                            <MenuItem value="warning">warning</MenuItem>
                            <MenuItem value="info">info</MenuItem>
                          </Select>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.25, fontSize: 12, color: tokens.neutral[700] }}>
                          <span>{meta.desc}</span>
                          {meta.thresholdField && (
                            <TextField
                              value={thrVal}
                              onChange={(e) => {
                                const v = e.target.value;
                                // optimistic update to keep the field responsive
                                setRules((prev) => prev.map((r) => r.id === a.id ? { ...r, conditions: { ...r.conditions, [meta.thresholdField]: v } } : r));
                              }}
                              onBlur={(e) => {
                                const v = e.target.value;
                                const n = parseFloat(v);
                                if (!isNaN(n)) {
                                  patchRule(a, { conditions: { ...a.conditions, [meta.thresholdField]: n } });
                                }
                              }}
                              size="small"
                              sx={{ width: 76, "& .MuiOutlinedInput-root": { height: 28, fontSize: 12, fontFamily: tokens.fontMono } }}
                            />
                          )}
                          {meta.unit && <span>{meta.unit}</span>}
                          <span style={{ marginLeft: 8, fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[500] }}>
                            {a.alertType}
                          </span>
                        </Stack>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", opacity: a.enabled ? 1 : 0.55 }}>
                        {CHANNELS_AVAILABLE.map((ch) => {
                          const on = (a.channels || []).includes(ch);
                          return (
                            <span
                              key={ch}
                              onClick={() => {
                                const next = on ? (a.channels || []).filter((c) => c !== ch) : [...(a.channels || []), ch];
                                patchRule(a, { channels: next });
                              }}
                              style={{
                                fontSize: 12,
                                padding: "3px 10px",
                                borderRadius: 999,
                                cursor: "pointer",
                                border: `1px solid ${on ? tokens.primaryIndigo : tokens.neutral[200]}`,
                                background: on ? tokens.primary[100] : "#fff",
                                color: on ? tokens.primaryIndigoInk : tokens.neutral[800],
                              }}
                            >
                              {ch}
                            </span>
                          );
                        })}
                      </Box>
                      <Button
                        onClick={() => deleteRule(a)}
                        disabled={savingIds.has(a.id)}
                        sx={{ ...dangerButtonStyle, minWidth: 36, px: 1 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Stack>
                  );
                })}

                {!loading && missing.length > 0 && (
                  <Box sx={{ p: 1.75, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mr: 1, alignSelf: "center" }}>
                      Add another condition:
                    </Typography>
                    {missing.map((t) => (
                      <Button key={t} sx={secondaryButtonStyle} onClick={() => createRule(t)}>
                        + {CONDITION_META[t]?.label || t}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Channels */}
              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}` }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>Channels</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                    Shared with the rest of Layerd. Connect at <code>/v2/settings/integrations</code>.
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5, p: 2 }}>
                  {CHANNEL_TILES.map((c) => {
                    const inUse = (rules || []).some((r) => (r.channels || []).includes(c.channel));
                    return (
                      <Box
                        key={c.name}
                        sx={{
                          border: `1px solid ${tokens.neutral[200]}`,
                          borderRadius: "8px",
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 34, height: 34, borderRadius: "8px", background: tokens.neutral[100], display: "grid", placeItems: "center", fontSize: 14 }}>
                            {c.name[0]}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{c.name}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: inUse ? "#1FC16B" : "#B0B0B0" }} />
                              <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                                {inUse ? "In use" : "Not routed to any MCP condition"}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* SIEM export */}
              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}` }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>SIEM export</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                    Per-endpoint stream of MCP events, shaped for Vector.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ p: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 280px", minWidth: 280 }}>
                    {Object.keys(siem).length === 0 && !loading && (
                      <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                        No endpoints registered on this site yet.
                      </Typography>
                    )}
                    {Object.entries(siem).map(([endpoint_id, row]) => (
                      <Stack
                        key={endpoint_id}
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ p: 1, borderBottom: `1px solid ${tokens.neutral[150]}` }}
                      >
                        <Switch
                          checked={!!row.siem_enabled}
                          onChange={() => toggleSiem(endpoint_id, !row.siem_enabled)}
                          size="small"
                          sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.primaryIndigo }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.primaryIndigo } }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <code style={{ fontFamily: tokens.fontMono, fontSize: 13 }}>{row.label || endpoint_id}</code>
                          <Typography sx={{ fontSize: 12, color: tokens.neutral[700], fontFamily: tokens.fontMono }}>
                            {row.siem_enabled ? "streaming → vector" : "not emitting"}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Box>
                  <Box sx={{ flex: "1 1 340px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 0.5 }}>
                      Output row preview
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        background: tokens.neutral[50],
                        border: `1px solid ${tokens.neutral[200]}`,
                        borderRadius: "8px",
                        fontFamily: tokens.fontMono,
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {SIEM_PREVIEW}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>
    </>
  );
}
